import { useTranslation } from 'react-i18next';
import Plot from 'react-plotly.js';

import PropTypes from 'prop-types';

import { COLORS } from './dataPlotConstants';

function UploadsChart({ allList, errList, interval, isMobile, isLoading, titleWithMargin, buildCardStyle }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'Overview' });
    const plotHeight = isMobile ? '260px' : '90%';

    if (isLoading) {
        return (
            <div className='card' style={buildCardStyle('large')}>
                <div style={titleWithMargin}>{t('perDayUpload')}</div>
                <div
                    className='animate-pulse'
                    style={{ width: '100%', height: plotHeight, padding: isMobile ? '0 16px 16px' : '0 50px 30px' }}
                >
                    {/* A ghost of the chart's own shape reads as "lines are coming" where a
                        plain block only reserves space. The real plot draws straight segments
                        between daily points, so the placeholder is a polyline too. */}
                    <svg
                        data-testid='chart-ghost'
                        width='100%'
                        height='100%'
                        viewBox='0 0 400 120'
                        preserveAspectRatio='none'
                        role='presentation'
                        aria-hidden='true'
                    >
                        <polyline
                            points='0,96 57,70 114,46 171,57 228,73 285,50 342,29 400,17'
                            fill='none'
                            stroke={COLORS.border}
                            strokeWidth='3'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                            vectorEffect='non-scaling-stroke'
                        />
                    </svg>
                </div>
            </div>
        );
    }

    return (
        <div className='card' style={buildCardStyle('large')}>
            <div style={titleWithMargin}>{t('perDayUpload')}</div>
            <Plot
                data={[
                    {
                        type: 'scatter',
                        mode: 'lines',
                        name: t('uploadCount'),
                        marker: {
                            color: COLORS.primaryLight,
                        },
                        line: {
                            color: COLORS.primary,
                            width: 2,
                        },
                        x: allList.map((item: any) => item.time),
                        y: allList.map((item: any) => item.count),
                    },
                    {
                        type: 'scatter',
                        mode: 'lines',
                        name: t('errorCount'),
                        marker: {
                            color: COLORS.errorLight,
                        },
                        line: {
                            color: COLORS.error,
                            width: 2,
                        },
                        x: errList.map((item: any) => item.time),
                        y: errList.map((item: any) => item.count),
                    },
                ]}
                layout={{
                    barmode: 'stack',
                    xaxis: {
                        tickformat: '%m/%d',
                        dtick: 86400000.0 * interval,
                    },
                    margin: isMobile ? { l: 40, r: 16, b: 40, t: 60, pad: 4 } : { l: 50, r: 50, b: 50, t: 30, pad: 4 },
                    legend: {
                        orientation: 'h',
                        x: 1,
                        y: 1.12,
                        xanchor: 'right',
                        yanchor: 'bottom',
                        font: {
                            size: isMobile ? 12 : 14,
                            color: COLORS.text,
                        },
                        bgcolor: 'rgba(255,255,255,0)',
                    },
                    autosize: true,
                }}
                config={{ responsive: true, staticPlot: true, displayModeBar: false }}
                style={{ width: '100%', height: plotHeight }}
                useResizeHandler
            />
        </div>
    );
}

UploadsChart.propTypes = {
    allList: PropTypes.array.isRequired,
    errList: PropTypes.array.isRequired,
    interval: PropTypes.number.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool,
    titleWithMargin: PropTypes.object.isRequired,
    buildCardStyle: PropTypes.func.isRequired,
};

export default UploadsChart;
